import axios from "axios";
import {
  FormRow,
  Input,
  Modal,
  ToggleButton,
  ToggleSwitch,
  Button,
} from "globalComponents";
import { errorHandler } from "globalResources/util";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { addNotification } from "../../../store/slices/uiSlice";
import { notifTags } from "globalComponents/Notification";
import { getTheme } from "Theme";

const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  width: 800px;
  padding: 16px;
`;

const SectionTitle = styled.h3`
  margin: 18px 0 4px 0;
`;

const SectionHelp = styled.p`
  margin: 0 0 10px 0;
  opacity: 0.75;
`;

const PermissionList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
`;

const PermissionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$on ? theme.MAIN_COLOR : theme.INPUT_BORDER || "#ddd")};
  background: ${(props) => (props.$on ? "rgba(90, 150, 255, 0.08)" : "transparent")};
  transition: border-color 200ms ease, background-color 200ms ease;
`;

const PermissionDescription = styled.span`
  font-size: 13px;
  line-height: 1.35;
  opacity: 0.8;
  padding-left: 54px; /* lines up under the label, past the switch */
`;

// What each permission lets someone do, in plain words. Falls back to the
// permission's own description for anything not listed here.
const PERMISSION_INFO = {
  manageUsers: {
    label: "Manage users",
    description: "Add, invite, edit, import and disable users.",
  },
  manageGroups: {
    label: "Manage groups",
    description:
      "Create and edit groups and what they're allowed to do. Trusted users only.",
  },
  manageUserTimeSheets: {
    label: "Manage everyone's time",
    description:
      "See, add, edit and delete time entries for any user, and see who's clocked in.",
  },
  editPayRate: {
    label: "Edit pay rates",
    description: "Change pay and bill rates on time entries.",
  },
  manageClients: {
    label: "Manage clients",
    description: "Create and edit clients and their settings.",
  },
  manageReports: {
    label: "Manage reports",
    description: "Create, edit and delete reports and adjustments.",
  },
  setReportPaid: {
    label: "Mark reports paid",
    description: "Record payment on billed reports.",
  },
  managePayroll: {
    label: "Manage payroll",
    description:
      "Run payroll and see everyone's pay: payroll reports, payroll jobs and adding adjustments to payroll.",
  },
  resetReport: {
    label: "Reset reports",
    description: "Unbill a report so it can be changed and sent again.",
  },
  viewAnalytics: {
    label: "View analytics",
    description: "See and run analytics reports.",
  },
  manageBugs: {
    label: "Manage bug reports",
    description: "Triage, label and update bug reports.",
  },
  manageJoinRequests: {
    label: "Manage join requests",
    description:
      "See the company code and accept or decline requests to join the company.",
  },
  manageCompany: {
    label: "Company settings",
    description:
      "Change the company name, week start, timezone, and whether anyone can ask to join.",
  },
};
const infoFor = (permission) =>
  PERMISSION_INFO[permission.name] || {
    label: permission.name,
    description: permission.description || "",
  };

const defaultForm = {
  name: "",
  description: "",
  notifyBilled: false,
  notifyReview: false,
  users: [],
  permissions: [],
  team: [],
};

const defaultErrors = {
  ...defaultForm,
  notifyBilled: "",
  notifyReview: "",
  users: "",
  permissions: "",
};
const GroupEdit = ({ selected, onClose }) => {
  const dispatch = useDispatch();
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(defaultErrors);
  const [form, setForm] = useState(defaultForm);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    if (!selected) return;
    if (selected._id) setForm({ ...selected });
    else setForm(defaultForm);
  }, [selected]);

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [userSearch]);
  const fetchUsers = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/users`, {
        params: { search: userSearch, searchBy: "name" },
      })
      .then(async (res) => {
        setUserOptions(
          res.data.users.map((user) => ({
            label: `${user.firstName} ${user.lastName} (${user.accountType})`,
            value: JSON.stringify(user),
          })),
        );
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setErrors((old) => ({ ...old, general: message }));
      })
      .finally(() => setLoading(false));
  };

  const fetchPermissions = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/user/permissions`, {
        params: { full: true },
      })
      .then(async (res) => {
        // non-super users only get the permissions they have themselves,
        // which is also all the API lets them grant
        setPermissionOptions(
          res.data.permissions.map(({ id, name, description }) => ({
            id,
            name,
            description,
          })),
        );
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setErrors((old) => ({ ...old, general: message }));
      })
      .finally(() => setLoading(false));
  };

  const submit = () => {
    axios[selected._id ? "patch" : "post"](
      `${process.env.REACT_APP_API_URL}/group`,
      {
        id: selected._id,
        ...form,
      },
    )
      .then((res) => {
        onClose();
        dispatch(
          addNotification({
            type: notifTags.success,
            message: selected._id ? "Group saved" : "Group created",
          }),
        );
      })
      .catch(async (err) => {
        const { message } = await errorHandler(err);
        setErrors((old) => ({ ...old, general: message }));
        dispatch(addNotification({ type: notifTags.error, message }));
      })
      .finally(() => setLoading(false));
  };

  // on/off by name: permission records don't always have an _id, and
  // filtering on a missing _id removed every permission at once
  const togglePermission = (permission, on) => {
    setForm((old) => ({
      ...old,
      permissions: on
        ? old.permissions.some((p) => p.name === permission.name)
          ? old.permissions
          : [
              ...old.permissions,
              {
                id: permission.id,
                name: permission.name,
                description: permission.description,
              },
            ]
        : old.permissions.filter((p) => p.name !== permission.name),
    }));
    setErrors((old) => ({ ...old, permissions: "" }));
  };

  const updateForm = (value, name, isDelete) => {
    if (name === "users") {
      setForm((old) => ({
        ...old,
        team: isDelete
          ? old.team.filter((t) => t._id !== value)
          : old.team.concat([JSON.parse(value)]),
      }));
    } else if (isDelete) {
      setForm((old) => ({
        ...old,
        [name]: old[name].filter((i) => i !== value),
      }));
    } else setForm((old) => ({ ...old, [name]: value }));
    setErrors((old) => ({ ...old, [name]: "" }));
  };

  const disable = async () => {
    try {
      const res = await axios.patch("/group/archive", {
        id: selected._id,
      });
      onClose();
    } catch (err) {
      const { message } = await errorHandler(err);
      setErrors((old) => ({ ...errors, general: message }));
    }
  };

  if (!selected) return null;

  // everything they can grant, plus anything the group already has that they
  // can't (shown on, but locked)
  const grantable = new Set(permissionOptions.map((p) => p.name));
  const allPermissions = [
    ...permissionOptions,
    ...form.permissions.filter((p) => !grantable.has(p.name)),
  ].sort((a, b) => (a.id || 999) - (b.id || 999));
  const hasPermission = (name) => form.permissions.some((p) => p.name === name);

  return (
    <Modal onClose={onClose}>
      <Main>
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => updateForm(e.currentTarget.value, "name")}
          error={errors.name}
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => updateForm(e.currentTarget.value, "description")}
          error={errors.description}
        />
        <FormRow style={{ gap: "20px" }}>
          <ToggleButton
            label="Receive Billed Notifications"
            value={form.notifyBilled}
            onChange={() => updateForm(!form.notifyBilled, "notifyBilled")}
          />
          <ToggleButton
            label="Review Notifications"
            value={form.notifyReview}
            onChange={() => updateForm(!form.notifyReview, "notifyReview")}
          />
        </FormRow>
        <SectionTitle>Permissions</SectionTitle>
        <SectionHelp>Everyone in this group gets the permissions that are on.</SectionHelp>
        <PermissionList data-testid="group-permissions">
          {allPermissions.map((permission) => {
            const on = hasPermission(permission.name);
            const locked = !grantable.has(permission.name);
            const info = infoFor(permission);
            return (
              <PermissionRow key={permission.name} $on={on}>
                <ToggleSwitch
                  name={`permission-${permission.name}`}
                  label={info.label}
                  value={on}
                  disabled={locked}
                  onChange={(next) => togglePermission(permission, next)}
                />
                <PermissionDescription>
                  {info.description}
                  {locked && " You don't have this permission, so you can't change it."}
                </PermissionDescription>
              </PermissionRow>
            );
          })}
        </PermissionList>
        <FormRow>
          {selected._id && (
            <Button onClick={disable} color="grey">
              {selected.isArchived ? "Enable" : "Disable"}
            </Button>
          )}
          <Button onClick={submit}>Save Changes</Button>
        </FormRow>
      </Main>
    </Modal>
  );
};
export default GroupEdit;
